/* eslint-disable complexity */
import React from 'react';
import { LineChartConfig } from 'libs/end-to-end/src/line-chart';

// Constants
const DOT_RADIUS = 5;
const MIN_ARROW_DISTANCE = 50;
const DEFAULT_ARROW_COLOR = '#F07B54';
const TIP_TO_DOT_GAP = 6; // px gap from arrow tip to the end dot’s edge
const ARROW_HEAD_LEN = 8; // px length of each chevron arm from tip to base
const FIXED_HEAD_ANGLE_DEG = 45; // fixed angle of each arm relative to the shaft
const SHAFT_TO_TIP_OVERLAP = 0.5; // px the shaft overlaps *toward* the tip to avoid any visible gap

type XDatum = string | number | Date;
type YDatum = string | number | Date;
type Nullable<T> = T | null | undefined;

// Types for Nivo series data structure - compatible with ComputedDatum
interface SeriesDataPoint {
  data: {
    x?: Nullable<XDatum>;
    y?: Nullable<YDatum>;
  };
}

interface SeriesData {
  id: string | number;
  data: readonly SeriesDataPoint[];
}

type ScaleFn = (value: Nullable<XDatum | YDatum>) => number;

// Custom layer for drawing treatment arrow (single marker only)
interface TreatmentArrowLayerProps {
  series: readonly SeriesData[];
  xScale: ScaleFn;
  yScale: ScaleFn;
  config: LineChartConfig;
  innerHeight: number;
}

// Custom layer for drawing gradient area at treatment marker
interface TreatmentGradientLayerProps {
  series: readonly SeriesData[];
  xScale: ScaleFn;
  yScale: ScaleFn;
  config: LineChartConfig;
  innerHeight: number;
  shadowBeforeTreatmentDate: boolean;
}

const isFiniteNumber = (n: number): boolean => Number.isFinite(n);

const parseLocalYYYYMMDD = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!isFiniteNumber(year) || !isFiniteNumber(month) || !isFiniteNumber(day))
    return null;
  // Local date, midnight.
  const d = new Date(year, month - 1, day);
  // Validate rollovers (e.g. 2024-02-31).
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
};

const toXDate = (value: Nullable<XDatum>): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    return isFiniteNumber(value.getTime()) ? value : null;
  }
  if (typeof value === 'number') {
    const d = new Date(value);
    return isFiniteNumber(d.getTime()) ? d : null;
  }
  if (typeof value === 'string') {
    const local = parseLocalYYYYMMDD(value);
    if (local) return local;

    const d = new Date(value);
    return isFiniteNumber(d.getTime()) ? d : null;
  }
  return null;
};

const xKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

const sameXDay = (a: Date, b: Date): boolean => xKey(a) === xKey(b);

const toNumber = (value: Nullable<YDatum>): number | null => {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  // Date is not a valid Y datum for this chart usage.
  return null;
};

const getArrowMarker = (
  config: LineChartConfig,
): LineChartConfig['markers'][number] | undefined =>
  config.markers.find(
    (marker) => marker.id === 'treatment-arrow' && marker.arrow?.enabled,
  );

const getTreatmentMarker = (
  config: LineChartConfig,
): LineChartConfig['markers'][number] | undefined =>
  config.markers.find(
    (marker) => marker.id === 'treatment-line' && marker.gradientArea?.enabled,
  );

// Helper function to find a data point at a specific X day
const findPointAtX = (
  seriesData: SeriesData,
  targetX: Date,
): SeriesDataPoint | undefined => {
  return seriesData.data.find((d) => {
    const xd = toXDate(d.data.x);
    return xd != null && sameXDay(xd, targetX);
  });
};

// Helper function to get the next point after a given X day
const getNextPoint = (
  seriesData: SeriesData,
  currentX: Date,
): SeriesDataPoint | null => {
  const sortedData = [...seriesData.data]
    .map((p) => {
      const xd = toXDate(p.data.x);
      return xd ? { p, xd } : null;
    })
    .filter((v): v is { p: SeriesDataPoint; xd: Date } => v !== null)
    .sort((a, b) => a.xd.getTime() - b.xd.getTime());

  const idx = sortedData.findIndex((d) => sameXDay(d.xd, currentX));
  return idx >= 0 && idx < sortedData.length - 1 ? sortedData[idx + 1].p : null;
};

// Helper to check if two Y values indicate a jump
const hasJumpBetweenPoints = (
  y1: Nullable<YDatum>,
  y2: Nullable<YDatum>,
): boolean => {
  const n1 = toNumber(y1);
  const n2 = toNumber(y2);
  if (n1 == null || n2 == null) return false;
  return n1 !== n2;
};

// Helper function to render arrow SVG components
const renderArrow = (
  markerX: number,
  startY: number,
  endY: number,
  arrowColor: string,
  strokeWidth: number = 3,
) => {
  const showArrowhead = Math.abs(startY - endY) >= MIN_ARROW_DISTANCE;
  const dir = Math.sign(endY - startY) || 1; // +1 down, -1 up

  // Tip sits away from the end dot by DOT_RADIUS + desired visual gap
  const tipY = endY - dir * (DOT_RADIUS + TIP_TO_DOT_GAP);

  // Compute base points using a fixed head angle
  const theta = (FIXED_HEAD_ANGLE_DEG * Math.PI) / 180;
  const dx = ARROW_HEAD_LEN * Math.cos(theta);
  const dy = ARROW_HEAD_LEN * Math.sin(theta);

  const baseLeftX = markerX - dx;
  const baseRightX = markerX + dx;
  const baseY = tipY - dir * dy;

  // Extend the shaft almost all the way to the tip so it visually "meets" the chevron
  const shaftEndY = tipY - dir * SHAFT_TO_TIP_OVERLAP;

  return (
    <g shapeRendering="geometricPrecision">
      {/* Shaft */}
      <line
        x1={markerX}
        y1={startY}
        x2={markerX}
        y2={showArrowhead ? shaftEndY : endY}
        stroke={arrowColor}
        strokeWidth={strokeWidth}
        strokeLinecap="butt"
        vectorEffect="non-scaling-stroke"
      />

      {/* Open chevron head as a single path with a miter join to eliminate any tip gap */}
      {showArrowhead && (
        <path
          d={`M ${baseLeftX} ${baseY} L ${markerX} ${tipY} L ${baseRightX} ${baseY}`}
          fill="none"
          stroke={arrowColor}
          strokeWidth={strokeWidth}
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          strokeLinecap="butt"
          vectorEffect="non-scaling-stroke"
        />
      )}

      {/* End dots */}
      <circle cx={markerX} cy={startY} r={DOT_RADIUS} fill={arrowColor} />
      <circle cx={markerX} cy={endY} r={DOT_RADIUS} fill={arrowColor} />
    </g>
  );
};

const getArrowDirectionAtTreatment = (
  series: readonly SeriesData[],
  xScale: ScaleFn,
  yScale: ScaleFn,
  arrowMarker: LineChartConfig['markers'][number] | undefined,
): 'up' | 'down' | 'unknown' => {
  if (!arrowMarker?.arrow) return 'unknown';

  const markerDate = toXDate(arrowMarker.value as Nullable<XDatum>);
  if (!markerDate) return 'unknown';

  const { fromSeries, toSeries } = arrowMarker.arrow;

  const fromSeriesData = series.find((s) => s.id === fromSeries);
  const toSeriesData = series.find((s) => s.id === toSeries);
  if (!fromSeriesData || !toSeriesData) return 'unknown';

  const isSingle = fromSeries === toSeries;

  if (isSingle) {
    const treatmentPoint = findPointAtX(fromSeriesData, markerDate);
    const afterPoint = getNextPoint(fromSeriesData, markerDate);
    const y1 = toNumber(treatmentPoint?.data.y);
    const y2 = toNumber(afterPoint?.data.y);
    if (y1 == null || y2 == null) return 'unknown';

    const startYpx = yScale(y1);
    const endYpx = yScale(y2);
    return endYpx < startYpx ? 'up' : 'down';
  }

  const fromPoint = findPointAtX(fromSeriesData, markerDate);
  const toPoint = findPointAtX(toSeriesData, markerDate);
  const y1 = toNumber(fromPoint?.data.y);
  const y2 = toNumber(toPoint?.data.y);
  if (y1 == null || y2 == null) return 'unknown';

  const startYpx = yScale(y1);
  const endYpx = yScale(y2);
  return endYpx < startYpx ? 'up' : 'down';
};

const getArrowHeightAtTreatment = (
  series: readonly SeriesData[],
  yScale: ScaleFn,
  arrowMarker: LineChartConfig['markers'][number] | undefined,
): number | null => {
  if (!arrowMarker?.arrow) return null;

  const markerDate = toXDate(arrowMarker.value as Nullable<XDatum>);
  if (!markerDate) return null;

  const { fromSeries, toSeries } = arrowMarker.arrow;

  const fromSeriesData = series.find((s) => s.id === fromSeries);
  const toSeriesData = series.find((s) => s.id === toSeries);
  if (!fromSeriesData || !toSeriesData) return null;

  const isSingle = fromSeries === toSeries;

  if (isSingle) {
    const treatmentPoint = findPointAtX(fromSeriesData, markerDate);
    const afterPoint = getNextPoint(fromSeriesData, markerDate);
    const y1 = toNumber(treatmentPoint?.data.y);
    const y2 = toNumber(afterPoint?.data.y);
    if (y1 == null || y2 == null) return null;

    const startY = yScale(y1);
    const endY = yScale(y2);
    return Math.abs(endY - startY);
  }

  const fromPoint = findPointAtX(fromSeriesData, markerDate);
  const toPoint = findPointAtX(toSeriesData, markerDate);
  const y1 = toNumber(fromPoint?.data.y);
  const y2 = toNumber(toPoint?.data.y);
  if (y1 == null || y2 == null) return null;

  const startY = yScale(y1);
  const endY = yScale(y2);
  return Math.abs(endY - startY);
};

const hasJump = (
  fromSeriesData: SeriesData,
  toSeriesData: SeriesData,
  markerDate: Date,
  isSingleLine: boolean,
): boolean => {
  if (isSingleLine) {
    const treatmentPoint = findPointAtX(fromSeriesData, markerDate);
    const afterTreatmentPoint = getNextPoint(fromSeriesData, markerDate);
    if (!treatmentPoint || !afterTreatmentPoint) return true;

    return hasJumpBetweenPoints(
      treatmentPoint.data.y,
      afterTreatmentPoint.data.y,
    );
  }

  const fromPoint = findPointAtX(fromSeriesData, markerDate);
  const toPoint = findPointAtX(toSeriesData, markerDate);
  if (!fromPoint || !toPoint) return true;

  return hasJumpBetweenPoints(fromPoint.data.y, toPoint.data.y);
};

const hasJumpAtTreatment = (
  series: readonly SeriesData[],
  arrowMarker: LineChartConfig['markers'][number] | undefined,
): boolean => {
  if (!arrowMarker?.arrow || series.length === 0) return true;

  const markerDate = toXDate(arrowMarker.value as Nullable<XDatum>);
  if (!markerDate) return true;

  const { fromSeries, toSeries } = arrowMarker.arrow;

  const fromSeriesData = series.find((s) => s.id === fromSeries);
  const toSeriesData = series.find((s) => s.id === toSeries);
  if (!fromSeriesData || !toSeriesData) return true;

  const isSingleLineTrendBreak = fromSeries === toSeries;
  return hasJump(
    fromSeriesData,
    toSeriesData,
    markerDate,
    isSingleLineTrendBreak,
  );
};

// eslint-disable-next-line complexity
export const TreatmentArrowLayer = ({
  series,
  xScale,
  yScale,
  config,
}: TreatmentArrowLayerProps) => {
  const arrowMarker = getArrowMarker(config);

  if (!arrowMarker?.arrow) return null;

  const markerDate = toXDate(arrowMarker.value as Nullable<XDatum>);
  if (!markerDate) return null;

  const markerX = xScale(markerDate);
  const arrow = arrowMarker.arrow;
  const arrowColor = arrowMarker.color || DEFAULT_ARROW_COLOR;

  const fromSeriesData = series.find((s) => s.id === arrow.fromSeries);
  const toSeriesData = series.find((s) => s.id === arrow.toSeries);

  if (!fromSeriesData || !toSeriesData) return null;

  const isSingleLineTrendBreak = arrow.fromSeries === arrow.toSeries;

  if (isSingleLineTrendBreak) {
    const treatmentPoint = findPointAtX(fromSeriesData, markerDate);
    const afterTreatmentPoint = getNextPoint(fromSeriesData, markerDate);

    const y1 = toNumber(treatmentPoint?.data.y);
    const y2 = toNumber(afterTreatmentPoint?.data.y);
    if (y1 == null || y2 == null) return null;

    if (!hasJumpBetweenPoints(y1, y2)) return null;

    const startY = yScale(y1);
    const endY = yScale(y2);

    const afterDate = toXDate(afterTreatmentPoint?.data.x);
    const afterTreatmentX = afterDate ? xScale(afterDate) : markerX;

    const showArrowhead = Math.abs(startY - endY) >= MIN_ARROW_DISTANCE;

    return (
      <g>
        {showArrowhead && (
          <>
            {renderArrow(markerX, startY, endY, arrowColor)}
            {/* Horizontal dotted line from arrow end to next bucket data point */}
            <line
              x1={markerX}
              y1={endY}
              x2={afterTreatmentX}
              y2={endY}
              stroke={arrowColor}
              strokeWidth={2}
              strokeDasharray="3,3"
              opacity={0.7}
            />
          </>
        )}
        {!showArrowhead && renderArrow(markerX, startY, endY, arrowColor)}
      </g>
    );
  }

  const fromPoint = findPointAtX(fromSeriesData, markerDate);
  const toPoint = findPointAtX(toSeriesData, markerDate);

  const y1 = toNumber(fromPoint?.data.y);
  const y2 = toNumber(toPoint?.data.y);
  if (y1 == null || y2 == null) return null;

  if (!hasJumpBetweenPoints(y1, y2)) return null;

  const startY = yScale(y1);
  const endY = yScale(y2);
  const strokeWidth = arrowMarker.strokeWidth ?? 2;

  return renderArrow(markerX, startY, endY, arrowColor, strokeWidth);
};

// Custom layer for drawing gradient area at treatment marker to highlight the jump
// eslint-disable-next-line complexity
export const TreatmentGradientLayer = ({
  series,
  xScale,
  yScale,
  config,
  innerHeight,
  shadowBeforeTreatmentDate,
}: TreatmentGradientLayerProps) => {
  const treatmentMarker = getTreatmentMarker(config);
  if (!treatmentMarker?.gradientArea) return null;

  const arrowMarker = getArrowMarker(config);

  if (!hasJumpAtTreatment(series, arrowMarker)) return null;

  const gradientConfig = treatmentMarker.gradientArea;

  const treatmentDate = toXDate(treatmentMarker.value as Nullable<XDatum>);
  if (!treatmentDate) return null;

  // Get all unique X values from the data, preserving their original chronological order
  const xDatesInOrder: Date[] = [];
  const seen = new Set<string>();

  if (series.length > 0) {
    series[0].data.forEach((d) => {
      const xd = toXDate(d.data.x);
      if (!xd) return;
      const key = xKey(xd);
      if (seen.has(key)) return;
      seen.add(key);
      xDatesInOrder.push(xd);
    });
  }

  const treatmentIndex = xDatesInOrder.findIndex((d) =>
    sameXDay(d, treatmentDate),
  );

  let startDate: Date;
  let endDate: Date;

  if (shadowBeforeTreatmentDate) {
    // previous → treatment
    if (treatmentIndex > 0) {
      startDate = xDatesInOrder[treatmentIndex - 1];
      endDate = treatmentDate;
    } else {
      // no previous point; collapse or noop
      startDate = treatmentDate;
      endDate = treatmentDate;
    }
  } else {
    // treatment → next (current behavior)
    startDate = treatmentDate;

    if (treatmentIndex >= 0 && treatmentIndex < xDatesInOrder.length - 1) {
      endDate = xDatesInOrder[treatmentIndex + 1];
    } else {
      endDate = treatmentDate;
    }
  }

  const startX = xScale(startDate);
  const endX = xScale(endDate);
  const width = endX - startX;

  const minY = 0;
  const height = innerHeight;

  const gradientColor = '#FFFCF8'; // Light orange/cream color for gradient
  const borderColor = '#FFB298'; // Orange color for border lines
  const opacity = 0.8;

  const gradientId = 'treatment-gradient';

  const leftLineY1 = minY;
  const leftLineY2 = minY + height;

  return (
    <g>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop
            offset="0%"
            stopColor={gradientColor}
            stopOpacity={opacity * 1.5}
          />
          <stop offset="50%" stopColor={gradientColor} stopOpacity={opacity} />
          <stop
            offset="100%"
            stopColor={gradientColor}
            stopOpacity={opacity * 1.5}
          />
        </linearGradient>
      </defs>

      <rect
        x={startX}
        y={minY}
        width={width}
        height={height}
        fill={`url(#${gradientId})`}
        opacity={1}
      />

      <line
        x1={startX}
        y1={leftLineY1}
        x2={startX}
        y2={leftLineY2}
        stroke={borderColor}
        strokeWidth={1}
        opacity={0.6}
      />

      <line
        x1={endX}
        y1={minY}
        x2={endX}
        y2={minY + height}
        stroke={borderColor}
        strokeWidth={1}
        opacity={0.7}
      />
    </g>
  );
};
